<template>
  <div class="fixed inset-0 -top-6 z-50 overflow-hidden">
    <!-- Backdrop -->
    <Transition name="fade">
      <div 
        v-show="expandedItemId !== null" 
        class="absolute inset-0 bg-gray-500 bg-opacity-50 fade-in" 
        @click="$emit('close')"
      ></div>
    </Transition>
    
    <!-- Slide-over panel -->
    <Transition name="slide">
      <div 
        v-show="expandedItemId !== null" 
        class="absolute inset-y-0 right-0 max-w-[800px] w-full bg-white shadow-xl slide-in"
      >
        <div class="h-full flex flex-col">
          <!-- Header -->
          <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 class="text-lg font-medium text-gray-900 flex items-center">
              <SquarePen class="w-5 h-5 mr-2" />
              {{ panelTitle }}</h2>
            <button 
              class="p-1 rounded-md text-gray-400 hover:text-gray-500"
              @click="$emit('close')"
            >
              <X class="w-5 h-5" />
            </button>
          </div>
          
          <!-- Content -->
          <div class="flex-1 overflow-y-auto p-4">
            <div v-if="expandedItemId !== null && item">
              <!-- Review Statistics -->
              <div v-if="item?.review_stats" class="w-full max-w-[1000px] mx-auto mb-4 p-4 bg-white rounded-lg border-[1px] border-gray-200">
                <div 
                  class="relative cursor-pointer"
                  @click="toggleReviews"
                >
                  <!-- Header -->
                  <h3 class="text-sm font-medium text-gray-900">Review Statistics</h3>

                  <!-- Stats Content -->
                  <div class="mt-2">
                    <div class="flex items-center gap-4">
                      <div class="flex items-center gap-1">
                        <span class="text-yellow-500">★</span>
                        <span class="text-lg font-medium">{{ item.review_stats.average_rating.toFixed(1) }}</span>
                        <span class="text-gray-400 text-sm">({{ item.review_stats.total_reviews }} reviews)</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <template v-for="rating in [5, 4, 3, 2, 1]" :key="rating">
                          <div class="flex items-center gap-1">
                            <span class="text-sm text-gray-600">{{ rating }}★</span>
                            <div class="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                class="h-full bg-yellow-500" 
                                :style="{ width: `${(item.review_stats.star_counts[rating] / item.review_stats.total_reviews) * 100}%` }"
                              ></div>
                            </div>
                            <span class="text-sm text-gray-500">{{ item.review_stats.star_counts[rating] }}</span>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>

                  <!-- Chevron -->
                  <div class="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500">
                    <ChevronDown v-if="!isReviewsExpanded" class="w-5 h-5" />
                    <ChevronUp v-else class="w-5 h-5" />
                  </div>
                </div>

                <!-- Expanded Reviews Section -->
                <Transition name="expand">
                  <div v-if="isReviewsExpanded" class="mt-4 border-t pt-4">
                    <!-- Text only filter -->
                    <div class="flex items-center mb-4">
                      <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <Checkbox
                          v-model="showTextOnly"
                          id="text-only"
                        />
                        <span>Text reviews only</span>
                      </label>
                    </div>

                    <div v-if="isLoading" class="text-center py-4">
                      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    </div>
                    <div v-else-if="filteredReviews.length" class="space-y-4">
                      <div v-for="review in filteredReviews" :key="review.id" class="text-sm border-b pb-4 last:border-b-0">
                        <div class="flex items-start gap-4">
                          <!-- Adventurer Info -->
                          <router-link :to="`/profile/${review.adventurer.username}`" target="_blank" class="flex-shrink-0">
                            <img 
                              :src="review.adventurer.avatar_image" 
                              :alt="review.adventurer.first_name"
                              class="w-10 h-10 rounded-full object-cover"
                              @error="(e: Event) => {
                                const img = e.target as HTMLImageElement;
                                if (img) img.src = '/profile_photo.jpg';
                              }"
                            />
                          </router-link>
                          <div class="flex-grow">
                            <div class="flex items-center justify-between">
                              <div>
                                <div class="font-medium text-gray-900">
                                  {{ review.adventurer.first_name }} {{ review.adventurer.last_name }}
                                  <span class="text-sm text-gray-500 font-normal ml-1">@{{ review.adventurer.username }}</span>
                                </div>
                              </div>
                              <div class="text-sm text-gray-500">
                                {{ new Date(review.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }) }}
                              </div>
                            </div>
                            
                            <!-- Rating -->
                            <div class="mt-1 flex items-center gap-2">
                              <div class="flex items-center">
                                <span class="text-yellow-500">{{ '★'.repeat(review.stars_value) }}</span>
                                <span class="text-gray-300">{{ '★'.repeat(5 - review.stars_value) }}</span>
                              </div>
                            </div>

                            <!-- Review Text -->
                            <p v-if="review.review_text" class="mt-2 text-gray-700">
                              {{ review.review_text }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center py-4 text-gray-500">
                      No reviews available
                    </div>
                  </div>
                </Transition>
              </div>

              <PathStepForm 
                :path-step="item"
                :read-only="!canEdit"
                @saved="$emit('saved')" 
                @cancel="$emit('close')" 
              />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { X, ChevronDown, ChevronUp, SquarePen } from 'lucide-vue-next'
import { ref, computed } from 'vue'
import type { WeeklyTaskItem } from '@/types/curriculum'
import PathStepForm from './PathStepForm.vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { Checkbox } from '@/components/ui/checkbox'

const props = defineProps<{
  expandedItemId: number | null
  item: WeeklyTaskItem | undefined
  canEdit?: boolean
}>()

defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const isReviewsExpanded = ref(false)
const reviews = ref<any[]>([])
const isLoading = ref(false)
const showTextOnly = ref(false)

const canEdit = computed(() => props.canEdit !== false)
const panelTitle = computed(() => (canEdit.value ? 'Edit Task' : 'Task Details'))

const filteredReviews = computed(() => {
  if (!showTextOnly.value) return reviews.value;
  return reviews.value.filter(review => review.review_text?.trim());
});

const toggleReviews = async () => {
  isReviewsExpanded.value = !isReviewsExpanded.value
  
  if (isReviewsExpanded.value && props.item && !reviews.value.length) {
    isLoading.value = true
    try {
      const response = await api.get(API_ROUTES.GET_PATH_STEP_REVIEWS(props.item.id))
      reviews.value = response.data
    } catch (error) {
      console.error('Failed to fetch reviews:', error)
    } finally {
      isLoading.value = false
    }
  }
}
</script>

<style>
/* Existing styles */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease-out;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.slide-in {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

/* New expand transition */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-out;
  max-height: 1000px;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}
</style> 